import NextIcon from "@src/app/assets/icons/next.svg?react";
import PreviousIcon from "@src/app/assets/icons/previous.svg?react";
import RedantAvatarIcon from "@src/app/assets/icons/redant-avatar.svg?react";
import Button from "@src/app/components/Button/Button";
import ClientOnly from "@src/app/components/ClientOnly/ClientOnly";
import SocialShare from "@src/app/components/SocialShare/SocialShare";
import type { BFFArticleCard } from "@src/app/types/bff";
import cx from "classnames";
import dayjs from "dayjs";
import {
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import Link from "../Link";

type Props = Readonly<{
  videoArticle: BFFArticleCard;
  selectedVideoIndex: number;
  totalVideos?: number;
  isAutoplay?: boolean;
  showIndicator?: boolean;
  titleClampLines?: number;
  onPreviousVideo?: () => void;
  onNextVideo?: () => void;
  onEndedVideo?: () => void;
  onPlayVideo?: () => void;
  onPausedVideo?: () => void;
  onErrorVideo?: (event: YT.OnErrorEvent) => void;
}>;

export default function VideoPlayer({
  videoArticle,
  selectedVideoIndex,
  totalVideos,
  // isAutoplay,
  showIndicator = true,
  titleClampLines,
  onNextVideo,
  onPreviousVideo,
  onEndedVideo,
  onPlayVideo,
  onPausedVideo,
  onErrorVideo,
}: Props): ReactElement {
  const currentVideoNumber = (selectedVideoIndex || 0) + 1;
  const aspectRatio = 16 / 9;

  // Refs
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);

  // Create refs to hold latest callbacks
  const onEndedVideoRef = useRef(onEndedVideo);
  const onPlayVideoRef = useRef(onPlayVideo);
  const onPausedVideoRef = useRef(onPausedVideo);
  const onErrorVideoRef = useRef(onErrorVideo);

  // State Management
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const toggleDescriptionExpansion = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  // Keep refs updated whenever the corresponding props change
  /** 🧩 Article State */
  const {
    urlPath: articlePath,
    description: articleDesc,
    created,
    youtubeUrl = "",
    youtubeInfo: {
      id: youtubeInfoId,
      description: youtubeDesc,
      publishedAt,
      views = 0,
    } = {},
    authors = [],
    title,
    tags = [],
  } = videoArticle;

  // Combine sources for derived fields
  const youtubeId = youtubeInfoId || youtubeUrl.split("/embed/")[1] || "";
  const description = articleDesc || youtubeDesc || "";
  const dateSource = created || publishedAt;

  // Format date/time safely
  const publishedTime = dateSource ? dayjs(dateSource).format("HH:mm") : "";
  const publishedDate = dateSource
    ? dayjs(dateSource).format("YYYY年MM月DD日")
    : "";

  /** ----------------------------------------------
   * 🎬 YouTube Player Lifecycle (useEffect)
   * ----------------------------------------------
   * Handles dynamic loading and setup of the YouTube IFrame API.
   * Creates a `YT.Player` instance once the API is available and
   * binds all relevant player event callbacks.
   *
   * Responsibilities:
   *  - Load YouTube API script (if not already loaded)
   *  - Initialize player for the current `youtubeId`
   *  - Respond to player events (play, pause, end, error)
   *  - Maintain `videoArticleUrl` for sharing
   * ---------------------------------------------- */
  useEffect(() => {
    onEndedVideoRef.current = onEndedVideo;
  }, [onEndedVideo]);
  useEffect(() => {
    onPlayVideoRef.current = onPlayVideo;
  }, [onPlayVideo]);
  useEffect(() => {
    onPausedVideoRef.current = onPausedVideo;
  }, [onPausedVideo]);

  const handlePlayerStateChange = useCallback(
    (event: YT.OnStateChangeEvent) => {
      switch (event.data) {
        case YT.PlayerState.UNSTARTED:
          console.log("Video unstarted");
          break;
        case YT.PlayerState.ENDED:
          console.log("Video ended");
          onEndedVideoRef.current?.();
          break;
        case YT.PlayerState.PLAYING:
          console.log("Video is playing!");
          onPlayVideoRef.current?.();
          break;
        case YT.PlayerState.PAUSED:
          console.log("Video is paused!");
          onPausedVideoRef.current?.();
          break;
        case YT.PlayerState.BUFFERING:
          console.log("Video is buffering");
          break;
        case YT.PlayerState.CUED:
          console.log("Video cued");
          break;
        default:
          console.log("Unknown state");
      }
    },
    [],
  );

  useEffect(() => {
    const loadYouTubeAPI = () => {
      console.log("Loading YouTube IFrame API");
      if (!("YT" in window)) {
        // Create a script element to load the YouTube IFrame API
        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        document.body.appendChild(script);
        // The API will call window.onYouTubeIframeAPIReady when loaded
      } else {
        // If the API is already loaded, initialize the player
        initializePlayer();
      }
    };

    const initializePlayer = () => {
      if (
        iframeRef.current &&
        typeof window.YT !== "undefined" &&
        typeof window.YT.Player === "function"
      ) {
        // Destroy previous instance if exists
        playerRef.current?.destroy();

        console.log("Initializing Player");
        // Create a new YouTube player instance
        playerRef.current = new window.YT.Player(iframeRef.current.id, {
          videoId: youtubeId,
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            mute: 1,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (event) => {
              // Play the video when the player is ready
              console.log("Player is ready. Playing video...");
              event.target.playVideo();

              // Add custom allow attributes to the injected iframe
              const iframe = iframeRef.current?.querySelector("iframe");
              iframe?.setAttribute(
                "allow",
                "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
              );
            },
            onStateChange: handlePlayerStateChange,
            onError: (event) => {
              // Handle error state if needed
              console.error("Error occurred in YouTube player:", event.data);
              onErrorVideoRef.current?.(event);
            },
          },
        });
      }
    };

    // Set the callback for when the YouTube IFrame API is ready
    window.onYouTubeIframeAPIReady = () => {
      console.log("YouTube IFrame API is ready");
      initializePlayer();
    };

    // Load the YouTube IFrame API
    loadYouTubeAPI();

    return () => {
      // Cleanup: Destroy the player instance when the component unmounts or dependencies change
      if (playerRef.current) {
        console.log("Destroying YouTube Player instance");
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [articlePath, youtubeUrl, youtubeId, handlePlayerStateChange]);

  // Reset description expansion when article changes
  useEffect(() => {
    // Reset the description expansion state
    setIsDescriptionExpanded(false);
  }, [articlePath]);

  // Check if description is clamped
  useEffect(() => {
    if (descriptionRef.current) {
      const visibleHeight = descriptionRef.current.clientHeight;
      const scrollHeight = descriptionRef.current.scrollHeight;

      if (scrollHeight > visibleHeight) {
        setIsDescriptionClamped(true);
      } else {
        setIsDescriptionClamped(false);
      }
    }
  }, [description]);

  return (
    <div
      id="video-player"
      data-testid="video-player"
      className="bg-inverse px-3 py-4"
    >
      {/* Video IFrame or Placeholder */}
      {youtubeUrl ? (
        <div style={{ aspectRatio }}>
          <div ref={iframeRef} id="youtube-iframe" className="relative z-10" />
        </div>
      ) : (
        <div
          className="flex w-full items-center justify-center bg-[#282828] text-[22px] font-medium text-[#EEEEEE]"
          style={{
            aspectRatio,
          }}
        >
          <span>Oops! Missing or broken video URL.</span>
        </div>
      )}

      {/* Video Navigation Controls (Mobile Only) */}
      <div
        id="video-player-controls"
        className="mt-4 flex flex-wrap items-center justify-between lg:hidden"
      >
        <Button
          icon={<PreviousIcon />}
          size="medium"
          variant="ghost"
          className="text-primary-on-dark hover:text-button-secondary-label"
          onClick={onPreviousVideo}
          disabled={currentVideoNumber === 1}
        >
          上一个
        </Button>
        {showIndicator && (
          <span className="text-secondary-on-dark">
            {currentVideoNumber}/{totalVideos}
          </span>
        )}
        <Button
          icon={<NextIcon />}
          size="medium"
          variant="ghost"
          iconPosition="right"
          className="text-primary-on-dark hover:text-button-secondary-label"
          onClick={onNextVideo}
          disabled={currentVideoNumber === totalVideos}
        >
          下一个
        </Button>
      </div>

      {/* Video Context: Title, Description, Tags, Metadata */}
      <div id="video-player-context" className="my-4">
        {/* Title */}
        <h1
          className={cx(
            "text-header-2 text-primary-on-dark mb-4",
            titleClampLines ? `line-clamp-${titleClampLines}` : "",
          )}
        >
          {title}
        </h1>

        {/* Description */}
        <p className="mb-4">
          {/* Mobile: Read More Description */}
          {description && (
            <Fragment>
              <span
                ref={descriptionRef}
                className={cx(
                  "text-body-2 text-secondary-on-dark mt-3 lg:hidden",
                  isDescriptionExpanded ? "line-clamp-none" : "line-clamp-3",
                )}
              >
                {description}
              </span>

              {isDescriptionClamped && (
                <button
                  onClick={toggleDescriptionExpansion}
                  className={cx(
                    "lg:hidden text-secondary-on-dark hover:underline font-bold cursor-pointer",
                    isDescriptionExpanded ? "block mt-4 ml-0" : "",
                  )}
                >
                  {isDescriptionExpanded ? "收起" : "更多"}
                </button>
              )}
            </Fragment>
          )}

          {/* Desktop: Read More Description */}
          <span className="mt-3 hidden text-body-2 text-secondary-on-dark lg:inline">
            {isDescriptionExpanded || !description || description.length <= 200
              ? description
              : `${description.substring(0, 200)}...`}
            {description && description.length > 200 && (
              <button
                onClick={toggleDescriptionExpansion}
                className={cx(
                  "text-secondary-on-dark hover:underline font-bold cursor-pointer",
                  isDescriptionExpanded ? "block mt-4 ml-0" : "ml-1",
                )}
              >
                {isDescriptionExpanded ? "收起" : "更多"}
              </button>
            )}
          </span>
        </p>

        {/* Tags */}
        <div className="mb-4">
          {tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {tags.map((tag) => (
                <Button
                  size="medium"
                  variant="secondary"
                  key={tag.name + tag.id}
                >
                  <Link to={tag.urlPath || ""}>{tag.name}</Link>
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Metadata */}
        <p className="mt-4 flex flex-wrap text-body-2 font-medium text-secondary-on-dark">
          <span className="mr-6">{views} views</span>
          <ClientOnly>
            <span>
              <span className="mr-2">发布:</span>
              <span className="mr-2">{publishedDate}</span>
              <span className="mr-2">{publishedTime}</span>
            </span>
          </ClientOnly>
        </p>
      </div>

      {/* Author & Social Share */}
      <div
        id="video-player-author-share"
        className="flex flex-wrap items-start justify-between gap-4 xs:items-center"
      >
        <div className="flex flex-auto items-center gap-[22px]">
          {authors.map((author) => {
            const { urlPath, name } = author;
            return (
              <div
                key={urlPath}
                className="flex items-start gap-2 text-body-2 text-primary-on-dark md:items-center"
              >
                {/* {imageUrl ? (
                  <img
                    className="w-6 h-6 rounded-full"
                    src={imageUrl}
                    alt={name}
                  />
                ) : (
                  <RedantAvatarIcon height={24} width={24} />
                )} */}
                <RedantAvatarIcon
                  height={24}
                  width={24}
                  className="mt-1 flex-none md:mt-0"
                />

                <Link
                  to={author.urlPath || ""}
                  className="cursor-pointer text-body-2 font-medium text-primary-on-dark"
                >
                  {name}
                </Link>
              </div>
            );
          })}
        </div>

        <SocialShare
          isVideoArticle
          youtubeTitle={title}
          articleTitle={videoArticle.title}
          articleUrl={videoArticle.urlPath}
          className="flex-none"
        />
      </div>
    </div>
  );
}
